import { UntypedFormGroup } from "@angular/forms";
import { ClaimsAppRequest } from "../models/claims-app-request";
import { ClaimsAppSubmissionConstant } from "../models/claims-app-submission.constant";
import { IRepairFacility } from "../services/models/repair-facility";
import { UserContact } from "../services/models/user-contact";
import { ClaimItemsGroup } from "../models/claim-items-group";
import { ClaimsAppConcernCauseComponentListComponent } from "../claims-app-concern-cause-component-list/claims-app-concern-cause-component-list.component";
import { ConcernCausePart } from "../models/concern-cause-part";
import { CorrectionEstimate } from "../models/estimates/correction-estimate";
import { PartEstimate } from "../models/estimates/part-estimate";
import { LaborEstimate } from "../models/estimates/labor-estimate";
import { MaterialEstimate } from "../models/estimates/material-estimate";

export class ClaimsAppSubmissionMapper {
    public static mapCustomerForm(claimsAppRequest: ClaimsAppRequest, form: UntypedFormGroup): void {
        if (!form) return;

        claimsAppRequest.contractNumber = form.value.contractNumber?.trim();
        claimsAppRequest.customerFirstName = form.value.customerFirstName?.trim();
        claimsAppRequest.customerLastName = form.value.customerLastName?.trim();
        claimsAppRequest.vin = form.value.vin?.trim();
    }

    public static mapMechanicalBreakdownForm(claimsAppRequest: ClaimsAppRequest, form: UntypedFormGroup): void {
        if (!form) return;

        claimsAppRequest.breakdownDate = form.value.breakdownDate.toLocaleDateString("en-US");
        claimsAppRequest.currentOdometer = form.value.currentOdometer;
        claimsAppRequest.repairOrderNumber = form.value.repairOrderNumber?.trim();
        claimsAppRequest.IsDrivenIn = form.value.transportationMode == ClaimsAppSubmissionConstant.driveIn;
        claimsAppRequest.isCollision = form.value.isCollision ? form.value.isCollision : false;
        claimsAppRequest.hasModification = form.value.hasModification ? form.value.hasModification : false;
        claimsAppRequest.IsCommercialUse = form.value.IsCommercialUse ? form.value.IsCommercialUse : false;
        
    }

    public static mapConcernCausePartList(claimsAppRequest: ClaimsAppRequest, concernList: ClaimsAppConcernCauseComponentListComponent): void {
        let concernCauseParts: ConcernCausePart[] = [];
        
        concernList?.objectForms?.forEach(concern => {
            let corrections: CorrectionEstimate[] = [];
            concern.correctionEstimateList?.objectForms?.forEach(correction => {
                
                let partEstimates: PartEstimate[] = [];
                correction.estimateList?.partForms?.forEach(partEstimate => {
                    partEstimates.push(new PartEstimate(
                        partEstimate.selectedPartComponent?.code,
                        partEstimate.form.get('partNumber').value,
                        partEstimate.form.get('etaDays').value,
                        partEstimate.form.get('price').value,
                        partEstimate.form.get('quantity').value,
                        partEstimate.form.get('isOem').value,
                        partEstimate.form.get('description').value
                    ));
                }); 

                let laborEstimates: LaborEstimate[] = [];
                correction.estimateList?.laborForms?.forEach(laborEstimate => {
                    laborEstimates.push(new LaborEstimate(
                        laborEstimate.form.get('laborOperationId').value,
                        null, // Coverage Code.
                        laborEstimate.form.get('rate').value,
                        laborEstimate.form.get('hours').value,
                        laborEstimate.form.get('description').value
                    ));
                });

                let materialEstimates: MaterialEstimate[] = [];
                correction.estimateList?.materialForms?.forEach(materialEstimate => {
                    materialEstimates.push(new MaterialEstimate(
                        materialEstimate.form.get('partNumber').value,
                        materialEstimate.form.get('price').value,
                        materialEstimate.form.get('quantity').value,
                        materialEstimate.form.get('description').value
                    ));
                });

                corrections.push(new CorrectionEstimate(
                    correction.form.get('correction').value,
                    partEstimates,
                    laborEstimates,
                    materialEstimates
                ));
            });

            concernCauseParts.push(new ConcernCausePart(concern.form.get('concern').value,
                                                        concern.form.get('cause').value,
                                                        concern.partComponent,
                                                        corrections));
        });
        
        claimsAppRequest.ConcernCauseComponents = concernCauseParts;
    }
    
    public static mapRepairFacilityForm(claimRequest: ClaimsAppRequest, form: UntypedFormGroup): void {
        if (!form) return;

        const claimForm = form.value;
        if (!claimForm) return;

        claimRequest.repairFacilityName = claimForm.repairFacilityName?.trim();
        claimRequest.repairFacilityBusinessEmail = claimForm.businessEmailAddress?.trim();
        claimRequest.repairFacilityContactEmail = claimForm.contactEmailAddress?.trim();
        claimRequest.repairFacilityPhone = claimForm.phoneNumber?.trim();
        claimRequest.repairFacilityContact = claimForm.repairFacilityContactName?.trim();
        claimRequest.repairFacilityPreferredContactMethod = 'Phone';
        claimRequest.repairFacilityAddressLine1 = claimForm.streetAddress1?.trim();
        claimRequest.repairFacilityAddressLine2 = claimForm.streetAddress2?.trim();
        claimRequest.repairFacilityState = claimForm.state?.trim();
        claimRequest.repairFacilityCity = claimForm.city?.trim();
        claimRequest.repairFacilityZip = claimForm.zip?.trim();

        claimRequest.repairFacilityLaborRatePerHour = claimForm.laborRatePerHour?.trim();
        claimRequest.repairFacilityLaborManualUsed = claimForm.laborManualUsed?.trim();
        claimRequest.repairFacilityPreferredPayment = claimForm.preferredPaymentMethod?.trim();
    }

    public static mapRepairFacilityInterface(claimRequest: ClaimsAppRequest, repairFacility: IRepairFacility): void {
        if (!repairFacility) return;

        claimRequest.repairFacilityId = repairFacility.id;
        claimRequest.repairFacilityName = repairFacility.facilityName?.trim();
        claimRequest.repairFacilityBusinessEmail = repairFacility.emailAddress?.trim();
        claimRequest.repairFacilityPhone = repairFacility.phone?.trim();
        claimRequest.repairFacilityPreferredContactMethod = 'Phone';
        claimRequest.repairFacilityAddressLine1 = repairFacility.address.addressLine1?.trim();
        claimRequest.repairFacilityAddressLine2 = repairFacility.address.addressLine2?.trim();
        claimRequest.repairFacilityState = repairFacility.address.state?.trim();
        claimRequest.repairFacilityCity = repairFacility.address.city?.trim();
        claimRequest.repairFacilityZip = repairFacility.address.zip?.trim();

        claimRequest.repairFacilityLaborRatePerHour = repairFacility.laborRate.toString()?.trim();
        claimRequest.repairFacilityLaborManualUsed = repairFacility.laborManual?.trim();
        claimRequest.repairFacilityPreferredPayment = repairFacility.preferredPaymentMethod?.trim();
    }

    public static mapContact(claimRequest: ClaimsAppRequest, contact: UserContact): void {
        if (!contact) return;

        claimRequest.contactId = contact.id;
        claimRequest.repairFacilityContactEmail = contact.emailAddress?.trim();
        claimRequest.repairFacilityContact = contact.name?.trim();
    }
}